import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Users, PartyPopper, ArrowUpRight } from 'lucide-react';
import axios from 'axios';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CLD = 'https://res.cloudinary.com/amenitiz/image/upload/q_auto,f_auto,w_1200';

const ACC_TO_GALLERY = {
  'appartement-1': 'appartement-1',
  'appartement-2': 'appartement-2',
  'gite-seul': 'gite',
  'gite-salle': 'salle',
};

const easeLuxe = [0.16, 1, 0.3, 1];

const cardVariants = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: easeLuxe } },
};

export default function Accommodations() {
  const [accommodations, setAccommodations] = useState([]);
  const [imagesByPage, setImagesByPage] = useState({});
  const featureFlags = useFeatureFlags();
  const visibleAccommodations = useMemo(
    () => accommodations.filter(a => a.id !== 'gite-salle' || featureFlags.gite_salle_enabled),
    [accommodations, featureFlags.gite_salle_enabled],
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const accRes = await axios.get(`${BACKEND_URL}/api/accommodations`);
        setAccommodations(accRes.data);
        const pages = ['appartement-1', 'appartement-2', 'gite', 'salle'];
        const results = await Promise.allSettled(
          pages.map(p => axios.get(`${BACKEND_URL}/api/media/page/${p}`))
        );
        const grouped = {};
        pages.forEach((p, i) => {
          if (results[i].status === 'fulfilled') {
            const images = (results[i].value.data || []).filter(m => m.type === 'image');
            if (images.length > 0) grouped[p] = images[0];
          }
        });
        setImagesByPage(grouped);
      } catch {
        try {
          const accRes = await axios.get(`${BACKEND_URL}/api/accommodations`);
          setAccommodations(accRes.data);
        } catch { /* ignore */ }
      }
    };
    fetchData();
  }, []);

  const scrollToReservation = (e) => {
    e?.stopPropagation();
    const el = document.querySelector('#reservation');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const goToGallery = (accId) => {
    const filterKey = ACC_TO_GALLERY[accId] || 'salle';
    window.location.hash = `galerie-${filterKey}`;
    setTimeout(() => {
      const el = document.querySelector('#galerie');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 50);
  };

  function getAccImage(acc) {
    const pageName = ACC_TO_GALLERY[acc.id];
    const mediaItem = imagesByPage[pageName];
    if (mediaItem) {
      const f = mediaItem.files || {};
      const filename = f.desktop || f.mobile || f.thumb;
      if (filename) return `${BACKEND_URL}/api/media/file/${filename}`;
    }
    return `${CLD}/${acc.image}`;
  }

  return (
    <section id="hebergements" data-testid="accommodations-section" className="py-28 md:py-40 bg-[#F9F8F6]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.5 }}
          variants={{
            hidden: {},
            show: { transition: { staggerChildren: 0.1 } },
          }}
          className="mb-16 md:mb-24 max-w-3xl"
        >
          <motion.span variants={cardVariants} className="overline">Nos formules</motion.span>
          <motion.h2
            variants={cardVariants}
            data-testid="accommodations-title"
            className="font-['Cormorant_Garamond'] text-5xl md:text-7xl font-light text-[#1A1C19] mt-5 tracking-[-0.025em] leading-[0.95]"
          >
            Hebergements
            <span className="block italic text-[#5A6153]">d&apos;exception</span>
          </motion.h2>
          <motion.p
            variants={cardVariants}
            className="mt-8 text-[#5A6153] max-w-xl text-base md:text-lg leading-relaxed font-light"
          >
            Quatre formules pensees pour s&apos;adapter a chaque sejour &mdash; de l&apos;intimite d&apos;un appartement
            a la grandeur du gite complet avec salle de reception.
          </motion.p>
        </motion.div>

        {/* Bento grid : 2 cols, alternating with one larger card */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
          {visibleAccommodations.map((acc, i) => {
            // Tetris bento: first card spans 7 cols on desktop, rest split
            const layoutCls = i === 0
              ? 'md:col-span-7 md:row-span-2'
              : i === 3
                ? 'md:col-span-7'
                : 'md:col-span-5';
            const imgHeight = i === 0
              ? 'h-[280px] md:h-[480px] lg:h-[640px]'
              : 'h-[260px] md:h-[300px]';
            return (
              <motion.article
                key={acc.id}
                data-testid={`accommodation-card-${acc.id}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.9, ease: easeLuxe, delay: i * 0.08 }}
                className={`group relative overflow-hidden rounded-[1.5rem] md:rounded-[2rem] bg-white shadow-[0_8px_30px_rgba(26,28,25,0.04)] hover:shadow-[0_24px_60px_-12px_rgba(26,28,25,0.18)] transition-all duration-700 ${layoutCls}`}
              >
                {/* Image */}
                <button
                  type="button"
                  onClick={() => goToGallery(acc.id)}
                  aria-label={`Voir la galerie ${acc.title}`}
                  className={`relative block w-full ${imgHeight} overflow-hidden cursor-pointer`}
                >
                  <img
                    src={getAccImage(acc)}
                    alt={acc.title}
                    className="w-full h-full object-cover card-image-zoom"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent opacity-90 group-hover:opacity-100 transition-opacity duration-700" />

                  {/* Top corner badge */}
                  <div className="absolute top-5 left-5 md:top-7 md:left-7">
                    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[10px] font-semibold tracking-[0.18em] uppercase backdrop-blur-md ${
                      acc.type === 'event'
                        ? 'bg-[#CBA77A]/90 text-[#1A1C19]'
                        : 'bg-white/85 text-[#1A1C19]'
                    }`}>
                      {acc.type === 'event' ? 'Evenementiel' : 'Sejour'}
                    </span>
                  </div>

                  {/* Overlaid title at the bottom of the image */}
                  <div className="absolute bottom-0 left-0 right-0 p-6 md:p-9 pointer-events-none">
                    {acc.subtitle && (
                      <span className="block text-[#CBA77A] text-[9px] md:text-[10px] uppercase tracking-[0.24em] font-medium mb-2 line-clamp-1">
                        {acc.subtitle}
                      </span>
                    )}
                    <h3 className={`font-['Cormorant_Garamond'] text-white font-light tracking-[-0.02em] leading-[1.02] ${
                      i === 0 ? 'text-4xl md:text-6xl lg:text-7xl' : 'text-2xl md:text-3xl'
                    }`}>
                      {acc.title}
                    </h3>
                  </div>
                </button>

                {/* Detail panel */}
                <div className="p-6 md:p-9">
                  {acc.tagline && (
                    <p data-testid={`acc-tagline-${acc.id}`} className="text-[#CBA77A] text-sm italic tracking-wide mb-3">
                      {acc.tagline}
                    </p>
                  )}
                  <p className="text-[#5A6153] text-sm md:text-base leading-relaxed font-light mb-6">
                    {acc.description}
                  </p>

                  {/* Specs row */}
                  <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[#1A1C19]/65 text-xs mb-6 border-t border-black/[0.06] pt-5">
                    <div className="flex items-center gap-1.5">
                      <Users className="w-3.5 h-3.5 text-[#CBA77A]" strokeWidth={1.5} />
                      <span className="tracking-wide">{acc.capacity} couchages</span>
                    </div>
                    {acc.event_capacity > 0 && (
                      <>
                        <span className="w-px h-3 bg-black/15" />
                        <div className="flex items-center gap-1.5">
                          <PartyPopper className="w-3.5 h-3.5 text-[#CBA77A]" strokeWidth={1.5} />
                          <span className="tracking-wide">Salle jusqu&apos;a {acc.event_capacity} pers.</span>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Tags */}
                  {acc.features?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-7">
                      {acc.features.slice(0, 4).map((f) => (
                        <span
                          key={f}
                          className="text-[10px] uppercase tracking-[0.12em] text-[#5A6153] px-3 py-1 rounded-full border border-black/[0.07] bg-[#F9F8F6]"
                        >
                          {f}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Bottom: price hint + CTA */}
                  <div className="flex items-end justify-between gap-4 flex-wrap">
                    <div>
                      <span className="block text-[10px] uppercase tracking-[0.22em] text-[#A69C8E] font-medium">
                        Tarif
                      </span>
                      <span className="block font-['Cormorant_Garamond'] text-2xl md:text-3xl font-light text-[#1A1C19] mt-1 italic">
                        sur devis
                      </span>
                      {acc.season_info && (
                        <span className="block text-[11px] text-[#5A6153]/80 mt-1 font-light">
                          {acc.season_info}
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      data-testid={`reserve-btn-${acc.id}`}
                      onClick={scrollToReservation}
                      className="group/btn inline-flex items-center gap-2 text-[#1A1C19] hover:text-[#CBA77A] text-[11px] font-semibold tracking-[0.22em] uppercase transition-colors duration-300 pb-1 border-b border-[#1A1C19]/30 hover:border-[#CBA77A]"
                    >
                      <span>Reserver</span>
                      <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" strokeWidth={2} />
                    </button>
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
